import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, TabContent, Box, Typography, ButtonIcon, Icon } from '@saviynt/design-system';
import AppLogo from 'ui-home/src/features/MainLayout/Header/AppLogo/AppLogo';
import { message } from '../../utils/helper';
import LinkButton from '../LinkButton/LinkButton';
import styles from './BrandingTabs.module.scss';

/**
 * Checks whether a file's extension matches the allowed acceptTypes string.
 * @param {File} file - The file to validate.
 * @param {string} acceptTypes - Comma-separated accepted extensions, e.g. ".png".
 * @returns {boolean} True if the file extension is allowed.
 */
const isValidFileType = (file, acceptTypes) => {
  const allowed = acceptTypes.split(',').map((ext) => ext.trim().toLowerCase().replace(/^\./, ''));
  const fileExt = file.name.split('.').pop()?.toLowerCase();

  return allowed.includes(fileExt);
};

/**
 * Logos tab content for BrandingConfig — favicon and login/logout logo uploads.
 */
const LogosTab = ({
  intl,
  classes,
  isReadOnly,
  isUploadDisabled,
  favIconImage,
  favIconFileName,
  uploadedFavIconFile,
  uploadedFaviconFileName,
  showFavIconContainer,
  onFavIconDeleteClick,
  onFavIconUpload,
  onFavIconFileDelete,
  loginLogoutLogoImage,
  uploadedLoginLogoutLogoFile,
  uploadedLoginLogoutFileName,
  showLoginLogoutLogoContainer,
  onLoginLogoutLogoEdit,
  onLoginLogoutLogoUpload,
  onLoginLogoutLogoFileDelete,
  setShowExampleModalVariant,
  activeTab,
}) => {
  const [editingFavIcon, setEditingFavIcon] = useState(false);
  const [editingLoginLogo, setEditingLoginLogo] = useState(false);

  const hasFavIcon = showFavIconContainer || uploadedFavIconFile?.length;
  const hasLoginLogo = showLoginLogoutLogoContainer || uploadedLoginLogoutLogoFile?.length;

  const favIconDisplayName = uploadedFavIconFile?.length
    ? uploadedFaviconFileName
    : favIconFileName;

  const loginLogoDisplayName = uploadedLoginLogoutLogoFile?.length
    ? uploadedLoginLogoutFileName
    : 'SaviyntR_White-Original.png';

  const getHeaderSection = (heading, subHeading, seeExampleOnClick) => (
    <Box className={styles.sectionHeader}>
      <Typography kind="h3">{heading}</Typography>
      <Typography kind="body2" color="--color-foreground-neutral-subtle">
        {subHeading}
        {seeExampleOnClick && (
          <>
            {' '}
            <LinkButton
              onClick={seeExampleOnClick}
              label={message(intl, 'UI.Gen2.Admin.Branding.Button.SeeExample')}
            />
          </>
        )}
      </Typography>
    </Box>
  );

  const getDivider = () => <Box className={styles.divider} />;

  return (
    <TabContent value={activeTab} tabValue="bc_tab_logos" className={classes.menuContent}>
      {/* Favicon section */}
      <Box className={styles.sectionWrapper}>
        {getHeaderSection(
          message(intl, 'UI.Gen2.Admin.Branding.Logo.FavIcon.Header'),
          message(intl, 'UI.Gen2.Admin.Branding.Logo.FavIcon.Description'),
          () => setShowExampleModalVariant('favIcon')
        )}
        <Box className={styles.sectionContent}>
          {hasFavIcon && !editingFavIcon ? (
            <Box
              className={`${styles.imageItemContainer} ${styles['imageItemContainer--autoHeight']}`}
            >
              <Box className={styles.imageItemHeader}>
                <span className={styles.imageItemFileName}>{favIconDisplayName}</span>
                {!isReadOnly && (
                  <ButtonIcon
                    size="medium"
                    kind="outlinedSecondary"
                    icon={<Icon kind="Edit" />}
                    onClick={() => setEditingFavIcon(true)}
                  />
                )}
              </Box>
              <Box
                className={`${styles.imageItemPreview} ${styles['imageItemPreview--square']} ${styles['imageItemPreview--contain']}`}
              >
                <AppLogo src={favIconImage} isSideNav />
              </Box>
            </Box>
          ) : editingFavIcon ? (
            <Box className={styles.imageItemEditing}>
              <Upload
                kind="dropzone"
                {...isUploadDisabled}
                isMultiple={false}
                onFileSelect={(files) => {
                  if (files[0] && isValidFileType(files[0], '.png')) {
                    onFavIconDeleteClick();
                    onFavIconUpload(files);
                    setEditingFavIcon(false);
                  }
                }}
                onFileDelete={onFavIconFileDelete}
                uploadedFiles={[]}
                acceptTypes=".png"
                fileTypesAllowedText={message(
                  intl,
                  'UI.Gen2.Admin.Branding.Logo.FavIcon.FileTypeAllowed'
                )}
                isReuploadWithDelete={false}
                showHeader={false}
                className={styles.uploadFullWidth}
              />
              <ButtonIcon
                size="medium"
                kind="outlinedSecondary"
                icon={<Icon kind="ChevronLeft" />}
                onClick={() => setEditingFavIcon(false)}
                className={styles.uploadCloseButton}
              />
            </Box>
          ) : (
            <Upload
              kind="dropzone"
              {...isUploadDisabled}
              isMultiple={false}
              onFileSelect={(files) => {
                if (files[0] && isValidFileType(files[0], '.png')) {
                  onFavIconUpload(files);
                }
              }}
              onFileDelete={onFavIconFileDelete}
              uploadedFiles={uploadedFavIconFile}
              acceptTypes=".png"
              fileTypesAllowedText={message(
                intl,
                'UI.Gen2.Admin.Branding.Logo.FavIcon.FileTypeAllowed'
              )}
              isReuploadWithDelete={false}
              showHeader={false}
            />
          )}
        </Box>
      </Box>

      {getDivider()}

      {/* Login/Logout Logo section */}
      <Box className={styles.sectionWrapper}>
        {getHeaderSection(
          message(intl, 'UI.Gen2.Admin.Branding.Logo.LoginLogout.Header'),
          message(intl, 'UI.Gen2.Admin.Branding.Logo.LoginLogout.Description'),
          () => setShowExampleModalVariant('brandLogo')
        )}
        <Box className={styles.sectionContent}>
          {hasLoginLogo && !editingLoginLogo ? (
            <Box className={styles.imageItemContainer}>
              <Box className={styles.imageItemHeader}>
                <span className={styles.imageItemFileName}>{loginLogoDisplayName}</span>
                {!isReadOnly && (
                  <ButtonIcon
                    size="medium"
                    kind="outlinedSecondary"
                    icon={<Icon kind="Edit" />}
                    onClick={() => setEditingLoginLogo(true)}
                  />
                )}
              </Box>
              <Box className={`${styles.imageItemPreview} ${styles['imageItemPreview--contain']}`}>
                <AppLogo src={loginLogoutLogoImage} isSideNav />
              </Box>
            </Box>
          ) : editingLoginLogo ? (
            <Box className={styles.imageItemEditing}>
              <Upload
                kind="dropzone"
                {...isUploadDisabled}
                isMultiple={false}
                onFileSelect={(files) => {
                  if (files[0] && isValidFileType(files[0], '.png')) {
                    onLoginLogoutLogoEdit();
                    onLoginLogoutLogoUpload(files);
                    setEditingLoginLogo(false);
                  }
                }}
                onFileDelete={onLoginLogoutLogoFileDelete}
                uploadedFiles={[]}
                acceptTypes=".png"
                fileTypesAllowedText={message(
                  intl,
                  'UI.Gen2.Admin.Branding.Logo.LoginLogout.FileTypeAllowed'
                )}
                isReuploadWithDelete={false}
                showHeader={false}
                className={styles.uploadFullWidth}
              />
              <ButtonIcon
                size="medium"
                kind="outlinedSecondary"
                icon={<Icon kind="ChevronLeft" />}
                onClick={() => setEditingLoginLogo(false)}
                className={styles.uploadCloseButton}
              />
            </Box>
          ) : (
            <Upload
              kind="dropzone"
              {...isUploadDisabled}
              isMultiple={false}
              onFileSelect={(files) => {
                if (files[0] && isValidFileType(files[0], '.png')) {
                  onLoginLogoutLogoUpload(files);
                }
              }}
              onFileDelete={onLoginLogoutLogoFileDelete}
              uploadedFiles={uploadedLoginLogoutLogoFile}
              acceptTypes=".png"
              fileTypesAllowedText={message(
                intl,
                'UI.Gen2.Admin.Branding.Logo.LoginLogout.FileTypeAllowed'
              )}
              isReuploadWithDelete={false}
              showHeader={false}
            />
          )}
        </Box>
      </Box>
    </TabContent>
  );
};

LogosTab.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  isUploadDisabled: PropTypes.shape({}).isRequired,
  favIconImage: PropTypes.string.isRequired,
  favIconFileName: PropTypes.string.isRequired,
  uploadedFavIconFile: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  uploadedFaviconFileName: PropTypes.string.isRequired,
  showFavIconContainer: PropTypes.bool.isRequired,
  onFavIconDeleteClick: PropTypes.func.isRequired,
  onFavIconUpload: PropTypes.func.isRequired,
  onFavIconFileDelete: PropTypes.func.isRequired,
  loginLogoutLogoImage: PropTypes.string.isRequired,
  uploadedLoginLogoutLogoFile: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  uploadedLoginLogoutFileName: PropTypes.string.isRequired,
  showLoginLogoutLogoContainer: PropTypes.bool.isRequired,
  onLoginLogoutLogoEdit: PropTypes.func.isRequired,
  onLoginLogoutLogoUpload: PropTypes.func.isRequired,
  onLoginLogoutLogoFileDelete: PropTypes.func.isRequired,
  setShowExampleModalVariant: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
};

export default LogosTab;
